import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { filterNil } from '@lycoris-nubila/ngx-lycoris';
import { FormControl, FormGroup } from '@ngneat/reactive-forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { SelectItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import {
	finalize,
	map,
	shareReplay,
	switchMap,
	take,
	tap,
} from 'rxjs/operators';

import { CoacheeMoodDialogComponent } from 'dialog/coachee-mood-dialog/coachee-mood-dialog.component';
import { MoodDescriptionForm } from 'dialog/mood-description-dialog/mood-description-dialog.model';
import { UserMoodFrequency } from 'user/model/user-mood-frequency';
import { UserApplicationPort } from 'user/port/user-application.port';

@Injectable()
@UntilDestroy()
export class MoodDescriptionDialogController {
	public i18n = 'presentation.common.dialog.mood-description-dialog';

	public frequencyOptions$: Observable<SelectItem[]>;
	public moodDescriptionForm: FormGroup<MoodDescriptionForm>;

	constructor(
		private _dialogService: DialogService,
		private _translateService: TranslateService,
		private _userApplicationPort: UserApplicationPort
	) {
		this.moodDescriptionForm = new FormGroup<MoodDescriptionForm>({
			frequency: new FormControl<UserMoodFrequency>(UserMoodFrequency.DAY, {
				validators: [Validators.required],
			}),
		});

		this._initObservables();
		this._initListeners();
	}

	public openCoacheeMoodDialog() {
		this._userApplicationPort
			.observeSessionUserId()
			.pipe(
				filterNil(),
				take(1),
				switchMap((sessionUserId) =>
					this._userApplicationPort.updateUser({
						moodFrequency: this.moodDescriptionForm.value.frequency,
						id: sessionUserId,
					})
				),
				finalize(() =>
					this._dialogService.open(CoacheeMoodDialogComponent, {
						header: this._translateService.instant(
							this.i18n + '.coacheeMoodDialog.header'
						),
						dismissableMask: true,
						width: '500px',
					})
				),
				untilDestroyed(this)
			)
			.subscribe();
	}

	private _initObservables() {
		this.frequencyOptions$ = this._translateService
			.stream(this.i18n + '.frequencyDropdown.options')
			.pipe(
				map((frequencies) =>
					Object.keys(UserMoodFrequency).map(
						(frequency) =>
							({
								label: frequencies[frequency],
								value: frequency,
							} as SelectItem)
					)
				),
				shareReplay({ bufferSize: 1, refCount: true })
			);
	}

	private _initListeners() {
		this._userApplicationPort
			.markMoodDescriptionShown()
			.pipe(untilDestroyed(this))
			.subscribe();

		this._userApplicationPort
			.observeSessionUser()
			.pipe(
				filterNil(),
				take(1),
				tap((user) =>
					this.moodDescriptionForm.patchValue({
						frequency: user.metadata.moodFrequency || UserMoodFrequency.DAY,
					})
				),
				untilDestroyed(this)
			)
			.subscribe();
	}
}
